<template>
  <v-card class="flex flex-col max-h-screen overflow-hidden">
    <!-- Scrollable Content -->
    <div class="">
      <ActitiveTabcomponent :tabs="mainTabs" :defaultTab="'lender'" @tab-changed="onTabChanged">
        <template #tab-buttons>
          <v-icon plain @click="closeDialog" class="cursor-pointer hover:bg-gray-300 rounded p-1">
            mdi-close
          </v-icon>
        </template>
        <template #lender>
          <v-card-text class="form-content">
            <v-form ref="partnerForm" v-model="isFormValid">
              <v-row>
                <!-- Partner Information -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editedPartner.partnerName"
                    label="Lender Name"
                    required
                    :rules="[(v) => !!v || 'Lender name is required']"
                    dense
                    outlined
                    hide-details
                    placeholder="ex: ABC Finance"
                    prepend-inner-icon="mdi-domain"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="editedPartner.partnerStatus"
                    :items="partnerStatusOptions"
                    label="Status"
                    item-text="status"
                    item-value="status"
                    required
                    :rules="[(v) => !!v || 'Status is required']"
                    dense
                    outlined
                    hide-details
                    :prepend-inner-icon="getStatusIcon(editedPartner.partnerStatus)"
                  ></v-select>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="editedPartner.partnerDescription"
                    label="Description"
                    rows="3"
                    auto-grow
                    dense
                    outlined
                    hide-details
                    placeholder="Brief description about the lender"
                    prepend-inner-icon="mdi-text-box-outline"
                  ></v-textarea>
                </v-col>

                <v-col cols="12" md="12">
                  <div class="document-upload mb-2">
                    <v-file-input
                      v-model="partnerDocument"
                      label="Upload Agreement Document"
                      prepend-inner-icon="mdi-paperclip"
                      prepend-icon=""
                      outlined
                      dense
                      placeholder="ex: pdf"
                      accept="image/*, application/pdf"
                      :show-size="1000"
                      hide-details
                    ></v-file-input>

                    <div v-if="editedPartner.documentUrl" class="mt-2 flex items-center">
                      <v-icon size="20" color="primary" class="mr-2"
                        >mdi-file-document-outline</v-icon
                      >
                      <a
                        :href="editedPartner.documentUrl"
                        target="_blank"
                        class="text-blue-600 hover:underline mr-2"
                      >
                        Current Document
                      </a>
                      <v-btn x-small icon color="error" @click="removePartnerDocument">
                        <v-icon x-small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="editedPartner.legalTerms"
                    label="Legal Terms and Conditions"
                    rows="5"
                    auto-grow
                    dense
                    outlined
                    hide-details
                    placeholder="Enter legal terms and conditions for the partnership"
                    prepend-inner-icon="mdi-file-document-outline"
                  ></v-textarea>
                </v-col>

                <!-- Revenue Sharing Section -->
                <v-col cols="12">
                  <div class="revenue-sharing-section border rounded p-3">
                    <h4 class="text-subtitle-1 mb-2">Revenue Sharing</h4>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model.number="hurdleRate"
                          label="Hurdle Rate"
                          type="number"
                          required
                          dense
                          outlined
                          hide-details
                          placeholder="ex: 25"
                          prepend-inner-icon="mdi-percent"
                          suffix="%"
                        >
                          <template v-slot:append>
                            <v-icon
                              v-tooltip="{
                                text: `${getHurdleTooltip}`,
                                width: '550px'
                              }"
                              color="primary"
                            >
                              mdi-information-outline
                            </v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="lenderRevenue"
                          label="Lender Revenue Share"
                          type="number"
                          required
                          :rules="[
                            (v) => !!v || 'Lender revenue share is required',
                            (v) => (v >= 0 && v <= 100) || 'Percentage must be between 0 and 100',
                            validateTotalRevenue
                          ]"
                          dense
                          outlined
                          hide-details
                          placeholder="ex: 75"
                          prepend-inner-icon="mdi-percent"
                          suffix="%"
                          @input="updateOurRevenue"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="ourRevenue"
                          :label="`${orgName} Revenue Share`"
                          type="number"
                          required
                          :rules="[
                            (v) => !!v || 'Our revenue share is required',
                            (v) => (v >= 0 && v <= 100) || 'Percentage must be between 0 and 100',
                            validateTotalRevenue
                          ]"
                          dense
                          outlined
                          hide-details
                          placeholder="ex: 25"
                          prepend-inner-icon="mdi-percent"
                          suffix="%"
                          @input="updateLenderRevenue"
                        ></v-text-field>
                      </v-col>
                    </v-row>
                    <div class="mt-2 text-right">
                      <span :class="totalRevenueValid ? 'text-success' : 'text-error'">
                        Total Revenue Share: {{ totalRevenue }}%
                        <v-icon x-small :color="totalRevenueValid ? 'success' : 'error'">
                          {{ totalRevenueValid ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                      </span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </template>
        <template #bulk>
          <div v-if="activeTab === 'bulk'">
            <GenericBulkUpload
              ref="bulkUploader"
              title="Bulk Upload Lenders"
              subtitle="Upload multiple lenders using Excel file"
              :entityName="'Lender'"
              :requiredColumns="['SL No.', 'partnerName', 'partnerDescription', 'partnerStatus']"
              :sampleData="[
                {
                  'SL No.': 1,
                  partnerName: 'ABC Finance',
                  partnerDescription: 'Financial Services',
                  partnerStatus: 'ACTIVE'
                },
                {
                  'SL No.': 2,
                  partnerName: 'XYZ Capital',
                  partnerDescription: 'Investment Bank',
                  partnerStatus: 'ACTIVE'
                }
              ]"
              @save-button-visibility="handleSaveButtonVisibility"
              uploadEndpoint="/workforce/ecom/partner/bulk"
              method="post"
              successKey="partnersCreatedSuccessfully"
              failedKey="partnerCreationFailed"
              :enableSave="false"
              :controlParentSaveButton="true"
              invalidItemsKey="invalidPartners"
              :showSaveButton="false"
              @close="closeUpload"
            />
          </div>
        </template>
      </ActitiveTabcomponent>
    </div>

    <!-- Fixed Footer -->
    <div class="sticky bottom-0 z-40 bg-white border-t border-gray-300 shadow-lg dark-bg-custom">
      <div class="flex items-center justify-end gap-3 p-4">
        <v-btn text class="normal-case min-w-24" @click="closeDialog"> Cancel </v-btn>
        <v-btn
          v-if="showSaveButton"
          :color="$vuetify.theme.currentTheme.primary"
          class="normal-case white--text min-w-32"
          :loading="isSubmitting"
          :disabled="!canSave || isSubmitting"
          @click="handleSave"
        >
          <v-icon left size="18">mdi-content-save</v-icon>
          Save
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script>
import ActitiveTabcomponent from '@/components/ActitiveTabcomponent.vue'
import GenericBulkUpload from '../CollectionManagement/GenericBulkUpload.vue'
import { axiosUrl } from '@/utils/workforceApi.js'
export default {
  name: 'AddEditPartner',
  components: {
    ActitiveTabcomponent,
    GenericBulkUpload
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    partnerData: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      orgName: this.$storage.getUniversal('organization_code'),
      isFormValid: false,
      isSubmitting: false,
      openBrands: false,
      activeTab: 'lender',
      showSaveButton: true, // Add this property to control save button visibility
      editedPartner: {
        _id: null,
        partnerName: '',
        partnerDescription: '',
        partnerStatus: 'ACTIVE',
        legalTerms: '',
        documentUrl: ''
      },
      hurdleRate: 0,
      lenderRevenue: 75,
      ourRevenue: 25,
      partnerDocument: null,
      partnerStatusOptions: [
        { status: 'ACTIVE', icon: 'mdi-check-circle' },
        { status: 'IN_ACTIVE', icon: 'mdi-close-circle' }
      ]
    }
  },
  computed: {
    mainTabs() {
      const tabs = [
        {
          label: `${this.isEditMode ? 'Edit' : 'Add'} Lender`,
          key: 'lender',
          icon: `${this.isEditMode ? 'mdi-pencil' : 'mdi-plus'}`,
          component: 'AddLender'
        },
        {
          label: `${this.isEditMode ? 'Bulk update' : 'Bulk Create'}`,
          key: 'bulk',
          icon: 'mdi-upload',
          component: 'GenericBulkUpload'
        }
      ]
      return tabs
    },
    getHurdleTooltip() {
      return `<ol>
      <li>* Minimum interest rate they must charge to make the loan profitable</li>
      <li>* Covers cost of funds + operational costs + risk premium + profit margin</li>
      </ol>`
    },
    isEditMode() {
      return !!this.editedPartner._id
    },
    totalRevenue() {
      return Number(this.lenderRevenue) + Number(this.ourRevenue)
    },
    totalRevenueValid() {
      return this.totalRevenue === 100
    },
    canSave() {
      if (this.activeTab === 'lender') {
        return this.isFormValid
      } else if (this.activeTab === 'bulk') {
        // For bulk upload, check if file is selected and valid
        return (
          this.$refs.bulkUploader?.selectedFile &&
          this.$refs.bulkUploader?.validationMessages?.length === 0
        )
      }
      return false
    }
    // saveButtonText() {
    //   if (this.activeTab === 'lender') {
    //     return this.isEditMode ? 'Update' : 'Save'
    //   } else if (this.activeTab === 'bulk') {
    //     return 'Upload'
    //   }
    //   return 'Save'
    // }
  },
  mounted() {
    this.initializeForm()
  },
  methods: {
    onTabChanged(tabKey) {
      this.activeTab = tabKey
      // Force re-evaluation of canSave when tab changes
      this.$forceUpdate()
    },
    handleSave() {
      if (this.activeTab === 'lender') {
        this.savePartnerDetails()
      } else if (this.activeTab === 'bulk') {
        this.processBulkUpload()
      }
    },
    async processBulkUpload() {
      if (!this.$refs.bulkUploader) {
        this.$toast.error('Bulk uploader not available')
        return
      }

      try {
        this.isSubmitting = true
        await this.$refs.bulkUploader.processFile()
        this.$toast.success('Bulk upload completed successfully')
        this.$emit('partner-saved')
        this.closeDialog()
      } catch (error) {
        console.error('Error processing bulk upload:', error)
        this.$toast.error('Failed to process bulk upload')
      } finally {
        this.isSubmitting = false
      }
    },
    initializeForm() {
      this.editedPartner = {
        _id: this.partnerData._id,
        partnerName: this.partnerData.partnerName || '',
        partnerDescription: this.partnerData.partnerDescription || '',
        partnerStatus: this.partnerData.partnerStatus || 'ACTIVE',
        legalTerms: this.partnerData.legalTerms || '',
        documentUrl: this.partnerData.documentUrl || ''
      }
      this.hurdleRate = this.partnerData?.revenueShare?.hurdleRate || 0
      this.lenderRevenue = this.partnerData?.revenueShare?.partnerShare || 75
      this.ourRevenue = this.partnerData?.revenueShare?.brokerShare || 25
    },
    getStatusIcon(status) {
      const statusObj = this.partnerStatusOptions.find((el) => el.status === status)
      return statusObj?.icon
    },
    closeDialog() {
      // Dialog visibility is controlled by parent component v-model
      // this.dialog = false  // This line is incorrect as 'dialog' is not in data
      this.resetForm()
      this.$emit('closeDialog')
    },
    uploadBrands() {
      this.openBrands = true
    },
    closeUpload() {
      this.openBrands = false
      // Switch back to lender tab after bulk upload
      this.activeTab = 'lender'
      this.$emit('partner-saved')
    },
    resetForm() {
      this.editedPartner = {
        _id: null,
        partnerName: '',
        partnerDescription: '',
        partnerStatus: 'ACTIVE',
        legalTerms: '',
        documentUrl: ''
      }
      this.lenderRevenue = 75
      this.ourRevenue = 25
      this.partnerDocument = null
      this.isFormValid = false
    },
    removePartnerDocument() {
      this.editedPartner.documentUrl = ''
    },
    handleSaveButtonVisibility(visible) {
      // This is called by GenericBulkUpload when its internal tabs change
      // We want to control the parent save button visibility based on the active tab
      // and the state of the GenericBulkUpload component
      if (this.activeTab === 'bulk') {
        // For bulk tab, the save button should be visible when a file is selected
        // and validation has been performed
        this.showSaveButton = visible
      } else {
        // For lender tab, the save button visibility is controlled by form validation
        this.showSaveButton = this.isFormValid
      }
    },
    updateLenderRevenue() {
      this.lenderRevenue = Math.max(0, Math.min(100, 100 - Number(this.ourRevenue)))
      this.editedPartner.revenueShare = this.lenderRevenue
    },
    updateOurRevenue() {
      this.ourRevenue = Math.max(0, Math.min(100, 100 - Number(this.lenderRevenue)))
      this.editedPartner.revenueShare = this.lenderRevenue
    },
    validateTotalRevenue() {
      return this.totalRevenue === 100 || 'Total revenue share must equal 100%'
    },
    async savePartnerDetails() {
      if (!this.$refs.partnerForm.validate()) return

      this.isSubmitting = true

      try {
        // Create FormData for file upload
        // const formData = new FormData()

        // Add partner data
        // formData.append('partnerName', this.editedPartner.partnerName)
        // formData.append('partnerDescription', this.editedPartner.partnerDescription)
        // formData.append('partnerStatus', this.editedPartner.partnerStatus)
        // formData.append('revenueShare', this.lenderRevenue) // Use lenderRevenue instead
        // formData.append('legalTerms', this.editedPartner.legalTerms)

        const payload = {
          partnerName: this.editedPartner.partnerName,
          partnerDescription: this.editedPartner.partnerDescription,
          partnerStatus: this.editedPartner.partnerStatus,
          revenueShare: {
            brokerShare: this.ourRevenue,
            partnerShare: this.lenderRevenue,
            hurdleRate: this.hurdleRate
          },
          legalTerms: this.editedPartner.legalTerms
        }

        let response
        if (this.isEditMode) {
          response = await this.$axios.put(
            `${axiosUrl.partners.addPartner}/${this.editedPartner._id}`,
            payload
          )
        } else {
          // Create new partner
          response = await this.$axios.post(axiosUrl.partners.addPartner, payload)
        }

        if (response.data.success) {
          this.$toast.success(
            this.isEditMode ? 'Lender updated successfully' : 'Lender added successfully'
          )
          this.$emit('partner-saved')
          this.closeDialog()
        } else {
          throw new Error(response.data.message || 'Failed to save lender')
        }
      } catch (error) {
        console.error('Error saving lender:', error)
        this.$toast.error(
          error.response?.data?.message || error.message || 'An error occurred while saving lender'
        )
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.form-content {
  max-height: 70vh;
  overflow-y: auto;
}
.document-upload {
  position: relative;
}
.revenue-sharing-section {
  border-color: rgba(0, 0, 0, 0.12);
}
.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  padding: 0px;
  color: #21427d;
  margin: 0;
}
</style>
